import api from '../../utils/axios';
import { useState, useCallback } from 'react';
import Swal from 'sweetalert2';
import { useMaintenanceApi } from './maintenanceApi';

interface UpdateDto extends EmpleadoDto {
  deleteImage?: boolean;
}

interface EmpleadoItem {
  Id: number;
  IdEPS: number;
  IdARL: number;
  IdAFP: number;
  IdCargo: number;
  IdSucursal: number;
  IdTipoDocumento: number;
  IdGenero: number;
  NumeroDocumento: string;
  Nombre: string;
  Apellidos: string;
  Telefono: string;
  Correo: string;
  Direccion: string;
  FechaIngreso: string;
  Imagen: string;
  Activo: boolean;
  Eliminado: boolean;
  eps?: { Id: number; Nombre: string };
  arl?: { Id: number; Nombre: string };
  afp?: { Id: number; Nombre: string };
  cargo?: { Id: number; Nombre: string };
  sucursal?: { Id: number; Nombre: string };
  tipoDocumento?: { Id: number; Nombre: string };
}

interface EmpleadoDto {
  IdEPS: number;
  IdARL: number;
  IdAFP: number;
  IdCargo: number;
  IdSucursal: number;
  IdTipoDocumento: number;
  IdGenero: number;
  NumeroDocumento: string;
  Nombre: string;
  Apellidos: string;
  Telefono: string;
  Correo: string;
  Direccion: string;
  FechaIngreso: string;
  Imagen?: string;
  Activo: boolean;
}

const useEmpleadosApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const baseUrl = '/administracion/empleados';

  const getAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.post<EmpleadoItem[]>(`${baseUrl}/findAll`);
      return response.data.filter(item => !item.Eliminado);
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getById = useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      const response = await api.post<EmpleadoItem>(`${baseUrl}/findOne/${id}`);
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const create = useCallback(async (data: EmpleadoDto) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/create`, data);
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const update = useCallback(async (id: number, data: UpdateDto) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/update/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const remove = useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      await api.post(`${baseUrl}/softDelete/${id}`);
      return true;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const uploadImage = useCallback(async (file: File) => {
    const formData = new FormData();
    formData.append('imagen', file);

    try {
      const response = await api.post(`${baseUrl}/upload-imagen`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.url;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }, []);

  const generoApi = useMaintenanceApi('generos');

  return {
    isLoading,
    getAll,
    getById,
    create,
    update,
    remove,
    uploadImage
  };
};

export { useEmpleadosApi };
export type { EmpleadoItem, EmpleadoDto };
